// frontend/src/components/NovaListaModal/NovaListaModal.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Segmented, App } from 'antd';
import dayjs from 'dayjs';
import { createListaCompra, getListasCompra } from '../../services/comprasService';
import { getErrorMessage } from '../../utils/errorHandler';

const fmtData = (d) => (d ? dayjs(String(d).slice(0, 10)).format('DD/MM/YYYY') : '—');

// Select que busca listas/modelos do local escolhido
function OrigemSelect({ localId, ehModelo, value, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    // modelos independem de local; "duplicar" precisa de um local escolhido
    if (!ehModelo && !localId) { setOpts([]); return; }
    const params = ehModelo ? { eh_modelo: 'true' } : { local: localId, eh_modelo: 'false' };
    getListasCompra(params)
      .then((rows) => {
        const filtradas = ehModelo
          ? rows
          : rows.filter((r) => ['CONCLUIDA', 'LANCADA'].includes(r.status));
        setOpts(filtradas.map((r) => ({
          value: r.id,
          label: `${r.titulo || 'Lista'} — ${fmtData(r.data_compra)}`,
        })));
      })
      .catch(() => setOpts([]));
  }, [localId, ehModelo]);

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={ehModelo ? 'Nenhum modelo salvo ainda' : 'Escolha o local primeiro'}
      notFoundContent="Nada encontrado"
      options={opts}
    />
  );
}

/**
 * Modal de "Nova lista" — compartilhado entre a listagem geral e a página de
 * detalhe do local (que já entra com o local pré-selecionado e travado).
 */
export default function NovaListaModal({ open, locais, localPadrao, onClose, onCreated }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      local: localPadrao ?? (locais.length === 1 ? locais[0].id : undefined),
      ponto_partida: 'branco',
      data_compra: dayjs(),
    });
  }, [open, localPadrao, locais, form]);

  const pontoPartida = Form.useWatch('ponto_partida', form);
  const localSelecionado = Form.useWatch('local', form);

  const criarLista = async (values) => {
    setSaving(true);
    try {
      const payload = {
        local: values.local ?? null,
        titulo: values.titulo?.trim() || '',
        data_compra: values.data_compra ? values.data_compra.format('YYYY-MM-DD') : null,
        ponto_partida: values.ponto_partida,
        origem: values.ponto_partida === 'branco' ? null : values.origem,
      };
      const nova = await createListaCompra(payload);
      message.success('Lista criada.');
      onCreated(nova);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao criar lista.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Nova lista"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={saving}
      okText="Criar lista"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={criarLista}>
        <Form.Item
          name="local"
          label="Local (opcional)"
          tooltip="Pode deixar em branco — você escolhe a loja ao ir comprar ou ao concluir. Útil se você usa a mesma lista em vários supermercados."
        >
          <Select
            allowClear
            disabled={localPadrao != null}
            placeholder="Escolher depois"
            options={locais.map((l) => ({ value: l.id, label: l.nome }))}
          />
        </Form.Item>
        <Form.Item name="titulo" label="Título">
          <Input placeholder="Deixe em branco para usar a data" maxLength={120} />
        </Form.Item>
        <Form.Item name="data_compra" label="Data da compra">
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="ponto_partida" label="Começar a partir de">
          <Segmented
            block
            options={[
              { label: 'Em branco', value: 'branco' },
              { label: 'Compra anterior', value: 'duplicar' },
              { label: 'Modelo', value: 'modelo' },
            ]}
          />
        </Form.Item>
        {pontoPartida !== 'branco' && (
          <Form.Item
            name="origem"
            label={pontoPartida === 'modelo' ? 'Modelo' : 'Compra anterior'}
            rules={[{ required: true, message: 'Escolha a lista de origem.' }]}
          >
            <OrigemSelect
              localId={pontoPartida === 'modelo' ? null : localSelecionado}
              ehModelo={pontoPartida === 'modelo'}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
